"use client";

import { Conversation, ConversationContent } from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import { MistakeText } from "@/components/ai-elements/mistake-text";
import { Response } from "@/components/ai-elements/response";

type AnalysisPart = {
	id: string;
	original_text: string;
	description?: string;
	corrected_text?: string;
};

type SampleMessage = {
	id: string;
	role: "user" | "assistant";
	content: string;
	analysisParts?: AnalysisPart[];
};

const sampleMessages: SampleMessage[] = [
	{
		id: "1",
		role: "assistant",
		content: "Hello! I'm your AI English tutor. Tell me about your day!"
	},
	{
		id: "2",
		role: "user",
		content: "",
		analysisParts: [
			{
				id: "correct",
				original_text: "Today I "
			},
			{
				id: "mistake-1",
				original_text: "go",
				description:
					"Use past tense when talking about actions that happened earlier today.",
				corrected_text: "went"
			},
			{
				id: "correct",
				original_text: " to "
			},
			{
				id: "mistake-2",
				original_text: "the work",
				description:
					"When referring to your workplace, use 'work' without the article 'the'.",
				corrected_text: "work"
			},
			{
				id: "correct",
				original_text: " and "
			},
			{
				id: "mistake-3",
				original_text: "meet",
				description: "Use past tense 'met' to describe an action that already happened.",
				corrected_text: "met"
			},
			{
				id: "correct",
				original_text: " my team."
			}
		]
	},
	{
		id: "3",
		role: "assistant",
		content:
			"Great job! I can see you're making progress. Here's the corrected version: 'Today I went to work and met my team.' Keep practicing!"
	},
	{
		id: "4",
		role: "user",
		content: "",
		analysisParts: [
			{
				id: "correct",
				original_text: "Thank you! I "
			},
			{
				id: "mistake-4",
				original_text: "have been study",
				description:
					"When using 'have been' (present perfect continuous), the verb should be in -ing form.",
				corrected_text: "have been studying"
			},
			{
				id: "correct",
				original_text: " English "
			},
			{
				id: "mistake-5",
				original_text: "since",
				description:
					"Use 'for' with a period of time (3 months). Use 'since' with a specific point in time (January).",
				corrected_text: "for"
			},
			{
				id: "correct",
				original_text: " 3 months. Do you have "
			},
			{
				id: "mistake-6",
				original_text: "any advice",
				description:
					"While grammatically correct, 'any tips' or 'some advice' would be more natural in this context.",
				corrected_text: "any tips"
			},
			{
				id: "correct",
				original_text: "?"
			}
		]
	},
	{
		id: "5",
		role: "assistant",
		content:
			"Excellent progress! You're doing great with complex tenses. Here are some tips: Practice speaking daily, even if it's just to yourself. Read English content that interests you. And most importantly, don't be afraid to make mistakes - they're how we learn!"
	}
];

export const SampleDialog = () => {
	return (
		<div className="p-2 bg-background/30 rounded-3xl">
			<Conversation>
				<ConversationContent>
					{sampleMessages.map((message) => {
						if (message.role === "assistant") {
							return (
								<Message from="assistant" key={message.id}>
									<MessageContent>
										<Response>{message.content}</Response>
									</MessageContent>
								</Message>
							);
						}

						if (message.analysisParts) {
							return (
								<Message from="user" key={message.id}>
									<MessageContent className="inline">
										<MistakeText
											analysisParts={message.analysisParts}
											messageId={message.id}
										/>
									</MessageContent>
								</Message>
							);
						}

						return (
							<Message from="user" key={message.id}>
								<MessageContent>
									<Response>{message.content}</Response>
								</MessageContent>
							</Message>
						);
					})}
				</ConversationContent>
			</Conversation>
		</div>
	);
};
